import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep as _cloneDeep } from 'lodash';
import Moment from 'moment';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';

import EditIcon from '@mui/icons-material/Edit';
import Auth from '../../../data/auth-service';

const useStyles = makeStyles(theme => ({
    card: {
        maxWidth: 345,
    },
    primary: {
        color: theme.palette.primary.main,
    },
    secondary: {
        color: theme.palette.secondary.main,
    },
    mitigationItem: {
        borderBottom: `1px solid ${theme.palette.primary.main}`,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    description: {
        whiteSpace: 'pre-wrap',
    }
}));

const IncidentMitigations = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const [incident, setIncident] = useState(_cloneDeep(props.incident));
    const [isEdit, setIsEdit] = useState(false);
    const [mitigations, setMitigations] = useState([]);
    const incidentPerms = Auth.permissions.get('Incidents');
    const [readonly] = useState(!incidentPerms.write);

    useEffect(() => {
        setIncident(props.incident);
        setMitigations(props.incident.data.files.filter(v => Boolean(v.mitigation)));
    }, [props]);

    const handleSetEdit = () => {
        setIsEdit(true);
    };

    const handleEditDescription = (e) => {
        const i = { ...incident };
        i.data.description = e.target.value;
        setIncident(i);
    };

    const handleCancelEdit = () => {
        setIsEdit(!isEdit);
        setIncident(incident);
    };

    const handleSaveEdit = () => {
        props.onIncidentChange(incident);
        setIsEdit(false);
    };

    return (
        <Box mt={2} mb={5}>
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Paper elevation={3}>
                        <Box p={2}>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Box display="flex">
                                            <Typography variant="h6" className={classes.primary}>{incident.status === 'resolved' ? 'Incident Summary' : 'Incident Description'}</Typography>
                                        </Box>
                                        <Box display={(incident.status === 'resolved' || isEdit) ? 'none' : 'flex'}>
                                            {!readonly && <Box mt={-1}>
                                                <IconButton color="primary" onClick={handleSetEdit} size="large"><EditIcon /></IconButton>
                                            </Box>}
                                        </Box>
                                    </Box>
                                    <Box display={isEdit ? 'none' : 'flex'}>
                                        <Box className={classes.description}>{(incident && incident.data && incident.data.description) || ''}</Box>
                                    </Box>
                                    <Box display={isEdit ? 'none' : 'flex'}>
                                        <Box mt={2} display={incident.data.comments && incident.data.comments.length ? 'block' : 'none'}>
                                            <Typography color="secondary">Resolve Comment</Typography>
                                            {incident.data.comments && incident.data.comments.map((v, i) => {
                                                return (
                                                    <Box key={'comments-'+i} mt={2} className={classes.description}>{v}</Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                    <Box width="100%" hidden={!isEdit}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            maxRows={4}
                                            value={incident.data.description}
                                            onChange={handleEditDescription}
                                        />
                                        <Box mt={2} display="flex" justifyContent="flex-end">
                                            <Box ml={2}><Button variant="contained" color="secondary" onClick={handleCancelEdit}>Cancel</Button></Box>
                                            <Box ml={2}><Button variant="contained" color="primary" onClick={handleSaveEdit}>Save</Button></Box>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid container item xs={12} justifyContent="space-between">
                                    <Grid item className={classes.primary}>Total Mitigations: <span className={classes.secondary}>{mitigations.length}</span></Grid>
                                    {/* <Grid item className={classes.primary}>Data Tuning: <span className={classes.secondary}>3</span></Grid>
                                    <Grid item className={classes.primary}>Zeek: <span className={classes.secondary}>3</span></Grid>
                                    <Grid item className={classes.primary}>Suricata: <span className={classes.secondary}>1</span></Grid>
                                    <Grid item className={classes.primary}>External: <span className={classes.secondary}>4</span></Grid> */}
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>
                {/* <Grid item xs={6}>
                    <Paper elevation={3}>
                        <Box p={2}>
                            <Typography variant="h6" className={classes.primary}>Bricata System</Typography>
                            {incident.data.mitigations.internal.map((v, i) => {
                                return (
                                    <Box key={"internal-mitigation-"+i} display="flex" justifyContent="space-between" className={classes.mitigationItem}>
                                        <Box className={classes.primary}>{v.date}</Box>
                                        <Box>{v.description}</Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Grid> */}
                <Grid item xs={12}>
                    <Paper elevation={3}>
                        <Box p={2}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="h6" className={classes.primary}>External Mitigations</Typography>
                                {!readonly && <Button variant="contained" color="primary" onClick={() => { props.onTabChange({}, 2) }}>Upload File</Button>}
                            </Box>
                            {mitigations.map((v, i) => {
                                return (
                                    <Box key={'external-mitigation-' + i} className={classes.mitigationItem}>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="h6" color="secondary">{v.link}</Typography>
                                            <Box className={classes.primary}>{Moment(v.timestamp).format(theme.date.short)}</Box>
                                        </Box>
                                        <Grid container>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <Typography color="primary">Mitigation</Typography>
                                                    {v.mitigation}
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <Typography color="primary">Description</Typography>
                                                    {v.description}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

IncidentMitigations.propTypes = {
    incident: PropTypes.object.isRequired,
    onIncidentChange: PropTypes.func.isRequired,
    onTabChange: PropTypes.func.isRequired,
};

export default IncidentMitigations;
